import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/**
 * Builders for coloured primitive parts. Every part is converted to a non-indexed geometry with
 * `position`, `normal` and `color` attributes only, so all parts attached to one joint can be merged
 * into a single mesh (one draw call per joint) using a vertex-coloured material.
 */
export interface PartOpts {
  pos?: [number, number, number];
  rot?: [number, number, number];
  scale?: [number, number, number];
}

const tmpColor = new THREE.Color();

function finish(
  g: THREE.BufferGeometry,
  color: THREE.ColorRepresentation,
  o: PartOpts,
): THREE.BufferGeometry {
  let geo = g.index ? g.toNonIndexed() : g;
  if (geo !== g) g.dispose();
  geo.deleteAttribute('uv');
  const m = new THREE.Matrix4().compose(
    new THREE.Vector3(...(o.pos ?? [0, 0, 0])),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(...(o.rot ?? [0, 0, 0]))),
    new THREE.Vector3(...(o.scale ?? [1, 1, 1])),
  );
  geo.applyMatrix4(m);
  tmpColor.set(color);
  const count = geo.getAttribute('position').count;
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    colors[i * 3] = tmpColor.r;
    colors[i * 3 + 1] = tmpColor.g;
    colors[i * 3 + 2] = tmpColor.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  if (!geo.getAttribute('normal')) geo.computeVertexNormals();
  return geo;
}

export const part = {
  box(
    w: number,
    h: number,
    d: number,
    color: THREE.ColorRepresentation,
    o: PartOpts = {},
  ): THREE.BufferGeometry {
    return finish(new THREE.BoxGeometry(w, h, d), color, o);
  },
  cyl(
    rTop: number,
    rBottom: number,
    h: number,
    color: THREE.ColorRepresentation,
    o: PartOpts = {},
    segs = 10,
  ): THREE.BufferGeometry {
    return finish(new THREE.CylinderGeometry(rTop, rBottom, h, segs, 1), color, o);
  },
  sphere(
    r: number,
    color: THREE.ColorRepresentation,
    o: PartOpts = {},
    w = 14,
    h = 10,
  ): THREE.BufferGeometry {
    return finish(new THREE.SphereGeometry(r, w, h), color, o);
  },
  hemi(
    r: number,
    color: THREE.ColorRepresentation,
    o: PartOpts = {},
    thetaLen = Math.PI / 2,
  ): THREE.BufferGeometry {
    return finish(new THREE.SphereGeometry(r, 14, 8, 0, Math.PI * 2, 0, thetaLen), color, o);
  },
  cone(
    r: number,
    h: number,
    color: THREE.ColorRepresentation,
    o: PartOpts = {},
    segs = 10,
  ): THREE.BufferGeometry {
    return finish(new THREE.ConeGeometry(r, h, segs, 1), color, o);
  },
  torus(r: number, tube: number, color: THREE.ColorRepresentation, o: PartOpts = {}): THREE.BufferGeometry {
    return finish(new THREE.TorusGeometry(r, tube, 6, 16), color, o);
  },
  /**
   * Surface of revolution around Y from [radius, y] points. Rendered double-sided (both windings are
   * emitted) so robes and sleeves look right from outside and from below.
   */
  lathe(
    points: [number, number][],
    color: THREE.ColorRepresentation,
    o: PartOpts = {},
    segs = 14,
  ): THREE.BufferGeometry {
    const g = new THREE.LatheGeometry(
      points.map(([r, y]) => new THREE.Vector2(r, y)),
      segs,
    );
    return doubleSided(finish(g, color, o));
  },
  dodeca(r: number, color: THREE.ColorRepresentation, o: PartOpts = {}, detail = 0): THREE.BufferGeometry {
    return finish(new THREE.DodecahedronGeometry(r, detail), color, o);
  },
  ico(r: number, color: THREE.ColorRepresentation, o: PartOpts = {}, detail = 0): THREE.BufferGeometry {
    return finish(new THREE.IcosahedronGeometry(r, detail), color, o);
  },
  extrude(
    shape: THREE.Shape,
    depth: number,
    color: THREE.ColorRepresentation,
    o: PartOpts = {},
    bevel = 0,
  ): THREE.BufferGeometry {
    const g = new THREE.ExtrudeGeometry(shape, {
      depth,
      bevelEnabled: bevel > 0,
      bevelThickness: bevel,
      bevelSize: bevel,
      bevelSegments: 1,
      steps: 1,
    });
    g.translate(0, 0, -depth / 2);
    return finish(g, color, o);
  },
};

export function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const valid = parts.filter(Boolean);
  const g = mergeGeometries(valid, false);
  for (const p of valid) p.dispose();
  if (!g) throw new Error('mergeGeometries failed');
  g.computeBoundingSphere();
  return g;
}

/** Shared vertex-coloured materials (one per surface type), reused by every character. */
export const sharedMaterials = {
  cloth: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.0 }),
  skin: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0.0 }),
  metal: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.32, metalness: 0.75 }),
};

export function shade(hex: string, amount: number): string {
  const c = new THREE.Color(hex);
  const hsl = { h: 0, s: 0, l: 0 };
  c.getHSL(hsl);
  c.setHSL(hsl.h, hsl.s, Math.min(1, Math.max(0, hsl.l + amount)));
  return `#${c.getHexString()}`;
}

/** Appends a reversed-winding copy (with flipped normals) so a thin surface is visible from both sides. */
export function doubleSided(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const pos = g.getAttribute('position');
  const nor = g.getAttribute('normal');
  const col = g.getAttribute('color');
  const n = pos.count;
  const P = new Float32Array(n * 6);
  const N = new Float32Array(n * 6);
  const C = new Float32Array(n * 6);
  P.set(pos.array as Float32Array, 0);
  N.set(nor.array as Float32Array, 0);
  C.set(col.array as Float32Array, 0);
  for (let t = 0; t < n; t += 3) {
    for (let k = 0; k < 3; k++) {
      const src = t + (k === 0 ? 0 : k === 1 ? 2 : 1);
      const dst = n + t + k;
      P[dst * 3] = pos.getX(src);
      P[dst * 3 + 1] = pos.getY(src);
      P[dst * 3 + 2] = pos.getZ(src);
      N[dst * 3] = -nor.getX(src);
      N[dst * 3 + 1] = -nor.getY(src);
      N[dst * 3 + 2] = -nor.getZ(src);
      C[dst * 3] = col.getX(src);
      C[dst * 3 + 1] = col.getY(src);
      C[dst * 3 + 2] = col.getZ(src);
    }
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(P, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(N, 3));
  out.setAttribute('color', new THREE.BufferAttribute(C, 3));
  g.dispose();
  return out;
}
